import React, { useRef } from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { ReactComponent as GbcbLogo } from '../../../public/assets/GBCB/greaterbanklogo.svg';
import { ReactComponent as GooglePayLogo } from '../../../public/assets/GBCB/gpay.svg';
import { ReactComponent as PhonePeLogo } from '../../../public/assets/GBCB/phonepe.svg';
import { ReactComponent as PaytmLogo } from '../../../public/assets/GBCB/paytm.svg';
import { ReactComponent as AirtelLogo } from '../../../public/assets/GBCB/airtel.svg';
import { ReactComponent as AmazonPayLogo } from '../../../public/assets/GBCB/amazonpay.svg';
import { ReactComponent as PoweredByLogo } from '../../../public/assets/GBCB/poweredBy.svg';
import { ReactComponent as DownloadIcon } from '../../../public/assets/downloadIcon.svg';
import { ReactComponent as ShareIcon } from '../../../public/assets/shareIcon.svg';
import QRCodeGenerator from './QRCodeGenerator';

const upiApps = [
  { component: GooglePayLogo, name: 'Google Pay' },
  { component: PaytmLogo, name: 'Paytm' },
  { component: PhonePeLogo, name: 'PhonePe' },
  { component: AirtelLogo, name: 'Airtel Payments Bank' },
  { component: AmazonPayLogo, name: 'Amazon Pay' }
];

export default function GbcbScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  const generatePDF = async () => {
    if (!cardRef.current) return null;

    const canvas = await html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    return pdf;
  };

  const handleDownloadPDF = async () => {
    const pdf = await generatePDF();
    if (pdf) pdf.save(`${merchantVpa}.pdf`);
  };

  const handleSharePDF = async () => {
    const pdf = await generatePDF();
    if (!pdf) return;

    const pdfBlob = pdf.output('blob');
    const file = new File([pdfBlob], `${merchantVpa}.pdf`, {
      type: 'application/pdf'
    });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Scan & Pay Card',
          text: 'Here is my Scan & Pay QR card.',
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      pdf.save(`${merchantVpa}.pdf`);
      alert('Sharing is not supported on this device/browser. PDF downloaded instead.');
    }
  };

  return (
    <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 500, mx: 'auto' }}>
      {/* Outer orange border */}
      <Box
        ref={cardRef}
        sx={{
          border: '8px solid #EF7E27',
          borderRadius: 8,
          boxShadow: 3,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            backgroundImage: 'url(/assets/GBCB/bharatco_bg.png)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            px: 8,
            py: 4,
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <GbcbLogo style={{ width: '80%', height: 'auto' }} />
          </Box>

          {/* Scan & Pay pill */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#EF7E27',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: 20,
                fontFamily: 'Montserrat',
                borderRadius: 6,
                px: 4,
                py: 1
              }}
            >
              Scan &amp; Pay
            </Box>
          </Box>

          {/* Merchant Name */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              fontFamily: 'Montserrat',
              color: '#000',
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            {merchantName}
          </Typography>

          {/* QR box */}
          <Box
            sx={{
              border: '1px solid #2e7d32',
              borderRadius: 4,
              backgroundColor: '#ffffff',
              width: 260,
              height: 260,
              mx: 'auto',
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <QRCodeGenerator qrString={qrString} size={200} />
          </Box>

          {/* VPA */}
          <Typography
            sx={{
              fontWeight: 600,
              fontFamily: 'Montserrat',
              color: '#000',
              mb: 1,
              wordBreak: 'break-all'
            }}
          >
            {merchantVpa}
          </Typography>

          {/* Divider with label */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Divider sx={{ flex: 1, borderColor: '#2e7d32' }} />
            <Typography
              sx={{
                px: 2,
                color: '#EF7E27',
                fontWeight: 700,
                fontFamily: 'Montserrat',
                whiteSpace: 'nowrap',
                fontSize: 14
              }}
            >
              PAY USING ANY UPI APP
            </Typography>
            <Divider sx={{ flex: 1, borderColor: '#2e7d32' }} />
          </Box>

          {/* UPI apps row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3
            }}
          >
            {upiApps.map(({ component: LogoComponent, name }, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={name}
              >
                <LogoComponent style={{ height: 48, width: 48 }} />
              </Box>
            ))}
          </Box>

          {/* Powered By */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <PoweredByLogo style={{ width: 140, height: 'auto' }} />
          </Box>
        </Box>
      </Box>

      {/* Buttons */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          endIcon={<DownloadIcon />}
          sx={{ p: 1, width: '50%' }}
        >
          Download
        </Button>
        <Button
          variant="outlined"
          onClick={handleSharePDF}
          endIcon={<ShareIcon />}
          sx={{ p: 1, width: '50%' }}
        >
          Share
        </Button>
      </Box>
    </Box>
  );
}
